<template>
  <div v-if="newUiEnabled" class="ui-multi-selector" :class="containerClass">
    <label v-if="mainInputLabel" :for="`main-input-${id}`" :class="labelClass">
      {{ mainInputLabel }}

      <Tooltip v-if="tooltip" :value="tooltip" />
    </label>

    <div class="multi-selector-row">
      <slot name="inputPrepend" />

      <UiMultiSelect
        v-if="multiple"
        :id="`main-input-${id}`"
        :model-value="selectedValuesAsArray"
        :options="optionsAsArray"
        :placeholder="placeholder"
        :disabled="disabled"
        :show-select-all="inFilterPanel"
        @update:model-value="onUiMultipleChange"
      />

      <UiSelect
        v-else
        :id="`main-input-${id}`"
        :model-value="selectedValuesAsArray[0] ?? null"
        :options="optionsAsArray"
        :placeholder="placeholder"
        :disabled="disabled"
        @update:model-value="onUiSingleChange"
      />

      <slot name="inputAppend" />
    </div>
  </div>

  <div v-else ref="multiSelectorWrapper" :class="containerClass">
    <label v-if="mainInputLabel" :for="`main-input-${id}`" :class="labelClass">
      {{ mainInputLabel }}

      <Tooltip v-if="tooltip" :value="tooltip" />
    </label>

    <div ref="multiSelectorContainer" class="multi-selector-container">
      <!-- Input -->
      <div class="multi-selector-input">
        <div
          :class="`multi-selector-input-group input-group ${inputGroupSizeClass} ${inputGroupClass}`"
        >
          <slot name="inputPrepend" />

          <input
            :id="`main-input-${id}`"
            ref="mainInput"
            type="text"
            class="form-control cursor-pointer"
            :placeholder="placeholder"
            :value="inputLabel"
            :disabled="disabled"
            readonly
            @focus="showDropdown"
            @click="showDropdown"
          />

          <slot name="inputAppend" />
        </div>
      </div>
    </div>

    <!-- Teleported dropdown -->
    <Teleport :to="teleportTarget">
      <div
        v-if="isDropdownVisible"
        ref="dropdown"
        :class="`multi-selector-dropdown-teleported text-muted ${dropdownSizeClass}`"
        :style="dropdownStyle"
      >
        <!-- Search container -->
        <div
          v-if="showSearch"
          ref="form-checkboxes"
          class="multi-selector-search-container px-0 px-sm-3"
        >
          <div :class="`multi-selector-search input-group ${searchInputGroupSizeClass} mb-3`">
            <!-- Search button -->
            <div class="input-group-text">
              <UiIcon fa="fa fa-search" />
            </div>

            <!-- Search input -->
            <input v-model="searchedQuery" class="form-control" :placeholder="$t('Search')" />

            <!-- Clear button -->
            <button
              class="input-group-text cursor-pointer"
              :title="$t('Clear')"
              @click.prevent="onClearSearchedQuery"
            >
              <UiIcon fa="fa fa-times" />
            </button>
          </div>
        </div>

        <!-- Enable/disable all options -->
        <div v-if="multiple && options?.length" class="mb-3">
          <b-form-checkbox
            :model-value="isToggleAllCheckboxChecked"
            :disabled="filteredOptions.every((option) => option.disabled)"
            @update:model-value="toggleAll"
          >
            {{ toggleAllCheckboxLabel }}
          </b-form-checkbox>
        </div>

        <!-- Options -->
        <template v-for="option in filteredOptions" :key="option.value">
          <div v-if="option.isGroupHeader" class="multi-selector-group-header mt-2">
            {{ option.text }}
          </div>

          <MultiselectLazyCheckboxContainer v-else root="form-checkboxes" class="mt-1">
            <b-form-checkbox
              :value="option.value"
              :model-value="selectedValuesAsArray"
              :disabled="option.disabled"
              @update:model-value="toggle(option)"
            >
              {{ option.text }}
            </b-form-checkbox>
          </MultiselectLazyCheckboxContainer>
        </template>

        <div v-if="!filteredOptions.length" class="text-center mt-3 mb-0 alert alert-warning">
          {{ $t("No options found") }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import i18n from "@/lib/i18n";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import Tooltip from "@/components/Tooltip.vue";
import MultiselectLazyCheckboxContainer from "@/components/MultiselectLazyCheckboxContainer.vue";
import { filterPanelSelectAllKey } from "@/components/newUi/tables/filterPanelContext";

const ITEMS_TO_SHOW = 2;

export default {
  name: "Multiselector",

  components: {
    MultiselectLazyCheckboxContainer,
    Tooltip,
  },

  inject: {
    inFilterPanel: { from: filterPanelSelectAllKey, default: false },
  },

  props: {
    id: {
      type: String,
      required: true,
    },

    modelValue: {
      type: [Array, String, Number, Boolean, null],
      default: () => [],
      required: false,
    },

    options: {
      type: Array,
      default: null,
      required: true,
    },

    containerClass: {
      type: String,
      default: "mb-3",
      required: false,
    },

    placeholder: {
      type: String,
      default: i18n.t("Select options"),
      required: false,
    },

    toggleAllCheckboxLabel: {
      type: String,
      default: i18n.t("All"),
      required: false,
    },

    mainInputLabel: {
      type: String,
      default: null,
      required: false,
    },

    labelClass: {
      type: String,
      default: null,
      required: false,
    },

    multiple: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    tooltip: {
      type: String,
      default: null,
    },

    maxHeight: {
      type: Number,
      default: 300,
    },

    showSearch: {
      type: Boolean,
      default: true,
    },

    inputGroupClass: {
      type: String,
      default: "",
      required: false,
    },

    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value),
    },
  },

  emits: ["update:modelValue", "hide"],

  setup() {
    return { newUiEnabled: useNewUiEnabled() };
  },

  data() {
    return {
      selectedValues: {},
      isDropdownVisible: false,
      searchedQuery: null,
      dropdownStyle: {},
      teleportTarget: "body",
    };
  },

  computed: {
    selectedValuesAsArray() {
      // If selectedValues is an array, filter out undefined values
      if (Array.isArray(this.selectedValues)) {
        return this.selectedValues.filter((value) => value !== undefined);
      }

      // Return empty array if selectedValues is not an object or is null
      if (!this.selectedValues || typeof this.selectedValues !== "object") {
        return [];
      }

      // Convert selectedValues object to an array, filtering out undefined values
      // Note: null values are kept as they are valid option values
      return Object.values(this.selectedValues).filter((value) => {
        return value !== undefined;
      });
    },

    optionsAsArray() {
      if (!this.options || !Array.isArray(this.options)) {
        return [];
      }

      return this.options.map((option) => {
        return {
          value: option.value,
          text: option.text,
          disabled: option.disabled || option.isGroupHeader || false,
          isGroupHeader: option.isGroupHeader || false,
        };
      });
    },

    inputLabel() {
      const enabledOptions = this.optionsAsArray.filter(
        (option) => !option.disabled && !option.isGroupHeader,
      );

      if (
        enabledOptions.length > ITEMS_TO_SHOW &&
        enabledOptions.length > 0 &&
        enabledOptions.every((option) => this.isOptionSelected(option.value))
      ) {
        return this.$t("All");
      }

      const selectedValues = this.selectedOptions.map((option) => option.text);

      if (selectedValues.length <= ITEMS_TO_SHOW) {
        return selectedValues.join(", ");
      }

      return (
        selectedValues.slice(0, ITEMS_TO_SHOW).join(", ") +
        ` ${this.$t("+ {count} others", { count: selectedValues.length - ITEMS_TO_SHOW })}`
      );
    },

    isToggleAllCheckboxChecked() {
      const enabledFilteredOptions = this.filteredOptions.filter(
        (option) => !option.disabled && !option.isGroupHeader,
      );
      if (enabledFilteredOptions.length === 0) return false;

      return enabledFilteredOptions.every((option) => this.isOptionSelected(option.value));
    },

    filteredOptions() {
      if (!this.searchedQuery) {
        return this.optionsAsArray;
      }

      return this.optionsAsArray.filter((option) =>
        option.text.toLowerCase().includes(this.searchedQuery.toLowerCase()),
      );
    },

    selectedOptions() {
      return this.optionsAsArray.filter((option) => this.isOptionSelected(option.value));
    },

    maxHeightWithPx() {
      return `${this.maxHeight}px`;
    },

    inputGroupSizeClass() {
      return this.size === "md" ? "" : `input-group-${this.size}`;
    },

    dropdownSizeClass() {
      return this.size === "md" ? "" : `multi-selector-dropdown--${this.size}`;
    },

    searchInputGroupSizeClass() {
      return this.size === "lg" ? "input-group-lg" : "input-group-sm";
    },
  },

  watch: {
    modelValue(newValue) {
      if (newValue === null || newValue === undefined) {
        this.selectedValues = {};
        return;
      }

      let value = newValue;

      if (!Array.isArray(newValue) && newValue) {
        value = [newValue];
      }

      // on repopulate on prop change
      if (newValue) {
        this.setDefaultValues(Array.from(value.values()));
      }
    },

    isDropdownVisible() {
      this.updateEventListeners();
      if (this.isDropdownVisible) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },
  },

  mounted() {
    if ((!Array.isArray(this.modelValue) && this.modelValue) || this.modelValue?.length) {
      this.setDefaultValues(this.modelValue);
    }

    this.updateEventListeners();
    window.addEventListener("scroll", this.updateDropdownPosition, true);
    window.addEventListener("resize", this.updateDropdownPosition);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
    window.removeEventListener("scroll", this.updateDropdownPosition, true);
    window.removeEventListener("resize", this.updateDropdownPosition);
  },

  methods: {
    updateEventListeners() {
      if (this.isDropdownVisible) {
        document.addEventListener("click", this.onClickOutside);
      } else {
        document.removeEventListener("click", this.onClickOutside);
      }
    },

    showDropdown() {
      if (!this.disabled) {
        this.isDropdownVisible = true;
      }
    },

    onClickOutside(event) {
      const dropdown = this.$refs.dropdown;
      if (
        (!this.$refs.multiSelectorContainer ||
          !this.$refs.multiSelectorContainer.contains(event.target)) &&
        (!dropdown || !dropdown.contains(event.target))
      ) {
        this.isDropdownVisible = false;
        this.searchedQuery = null;

        this.$emit("hide");
      }
    },

    emitCurrentValue() {
      this.$emit(
        "update:modelValue",
        this.multiple ? this.selectedValuesAsArray : this.selectedValuesAsArray[0],
      );
    },

    toggle(option) {
      // Single select mode
      if (!this.multiple) {
        // Handle null values properly
        if (option.value === null) {
          this.selectedValues = { null: null };
        } else {
          this.selectedValues = { [option.value]: option.value };
        }

        this.emitCurrentValue();

        this.isDropdownVisible = false;

        // Remove focus from input to allow re-triggering focus event
        this.$nextTick(() => {
          if (this.$refs.mainInput) {
            this.$refs.mainInput.blur();
          }
        });

        return;
      }

      // Multiple select mode
      const checked = this.isOptionSelected(option.value);

      if (checked) {
        delete this.selectedValues[option.value];
        this.emitCurrentValue();
        return;
      }

      this.selectedValues[option.value] = option.value;

      this.emitCurrentValue();
    },

    onUiMultipleChange(values) {
      this.reindexFromAssocArray(values);
      this.emitCurrentValue();
    },

    onUiSingleChange(value) {
      this.selectedValues = value === null ? { null: null } : { [value]: value };
      this.emitCurrentValue();
    },

    toggleAll(checked) {
      if (!checked) {
        this.selectedValues = {};
        this.emitCurrentValue();
        return;
      }

      // we want to toggle only visible items
      const tmp = this.filteredOptions
        .filter((item) => !item.disabled)
        .map((option) => option.value);
      // repopulate filtered
      this.reindexFromAssocArray(tmp);
      this.emitCurrentValue();
    },

    reindexFromAssocArray(array) {
      this.selectedValues = {};

      for (const value of array) {
        if (value === undefined) {
          continue;
        }

        // Handle null values properly
        if (value === null) {
          this.selectedValues[null] = null;
        } else {
          this.selectedValues[value] = value;
        }
      }
    },

    setDefaultValues(defaultValues) {
      // for multiple values
      if (Array.isArray(defaultValues)) {
        this.reindexFromAssocArray(defaultValues);
        return;
      }

      // for single value - handle null properly
      if (defaultValues === null) {
        this.selectedValues = { null: null };
      } else {
        this.selectedValues = { [defaultValues]: defaultValues };
      }
    },

    onClearSearchedQuery() {
      this.searchedQuery = null;
    },

    updateDropdownPosition() {
      if (!this.isDropdownVisible || !this.$refs.multiSelectorContainer) return;

      const container = this.$refs.multiSelectorContainer;
      const rect = container.getBoundingClientRect();
      const dropdownHeight = this.maxHeight;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Determine if dropdown should open upwards or downwards
      const shouldOpenUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      this.dropdownStyle = {
        position: "fixed",
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 150)}px`,
        zIndex: 99999,
        maxHeight: `${this.maxHeight}px`,
        overflowY: "auto",
        overflowX: "auto",
        backgroundColor: "white",
        border: "1px solid #e4e7ea",
        borderRadius: "10px",
        padding: "10px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      };

      if (shouldOpenUpwards) {
        this.dropdownStyle.bottom = `${viewportHeight - rect.top}px`;
        this.dropdownStyle.maxHeight = `${Math.min(this.maxHeight, spaceAbove - 10)}px`;
      } else {
        this.dropdownStyle.top = `${rect.bottom}px`;
        this.dropdownStyle.maxHeight = `${Math.min(this.maxHeight, spaceBelow - 10)}px`;
      }
    },

    // Helper method to safely check if an option is selected
    isOptionSelected(optionValue) {
      // For better compatibility, we check both the array and the object
      return (
        this.selectedValuesAsArray.includes(optionValue) ||
        // eslint-disable-next-line no-prototype-builtins
        this.selectedValues.hasOwnProperty(optionValue)
      );
    },
  },
};
</script>

<style lang="scss">
.multi-selector-container {
  position: relative;
  min-width: 150px;

  .multi-selector-input input {
    background-color: white !important;
    min-width: 80px;

    &:disabled {
      background-color: #e4e7ea;
    }

    &:focus {
      border-color: #e4e7ea;
      box-shadow: none;
    }
  }
}

.multi-selector-dropdown-teleported {
  background-color: white;
  border: 1px solid #e4e7ea;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 99999 !important;

  .multi-selector-search-container .multi-selector-search {
    border: 1px solid #d3d3d3;
    border-radius: 10px;

    input,
    .input-group-text {
      border: none;
      border-radius: 10px;
    }
  }

  .form-check {
    word-wrap: break-word;
    overflow-wrap: break-word;

    .form-check-label {
      white-space: normal;
      line-height: 1.3;
    }
  }

  .multi-selector-group-header {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #8a93a2;
    border-bottom: 1px solid #e4e7ea;
    padding-bottom: 4px;

    &:first-child {
      margin-top: 0 !important;
    }
  }

  &.multi-selector-dropdown--lg {
    .form-check {
      padding-left: 2rem;
    }

    .form-check-label {
      font-size: 1.125rem;
    }

    .form-check-input {
      width: 1.35rem;
      height: 1.35rem;
      margin-top: 0.2rem;
      margin-left: -2rem;
    }

    .multiselect-lazy-checkbox-placeholder {
      min-height: 2rem;
    }
  }
}
</style>
